import type { SimEvent } from '../shared/contract.js';

/**
 * Four currencies, one debt clock (economy.md).
 *
 * MÜNZEN pay debts, TICKETS buy futures and cannot be taxed, GLÜCK bends declared rolls
 * and regenerates on losses (pity as a visible mechanic, never a secret thumb on the
 * scale), VERDACHT is not spent but owed.
 */

export const DEBT_CURVE: readonly number[] = [50, 120, 300, 700, 1600, 3500, 8000, 20000];
export const GLUECK_MAX = 10;
export const VERDACHT_MAX = 100;

export const VERDACHT_THRESHOLDS = {
  gezinkt: 30,
  rausschmeisser: 60,
  hausverbot: 90,
} as const;

/** Floor multiplier in the payout pipeline — why depth pays. */
export function floorMultiplier(floor: number): number {
  return 1 + 0.5 * (Math.max(1, Math.min(8, floor)) - 1);
}

export function debtFor(floor: number): number {
  return DEBT_CURVE[Math.max(0, Math.min(DEBT_CURVE.length - 1, floor - 1))]!;
}

export class Economy {
  muenzen: number;
  tickets = 0;
  glueck = 3;
  verdacht = 0;
  floor = 1;
  /** pity accumulator: losses buy back GLÜCK at a visible rate */
  private pity = 0;
  /** rate: this many dud events regenerate 1 GLÜCK */
  readonly pityPerGlueck = 3;

  constructor(startingCoins = 100) {
    this.muenzen = startingCoins;
  }

  get debtDue(): number {
    return debtFor(this.floor);
  }

  addCoins(n: number): void {
    this.muenzen = Math.max(0, this.muenzen + Math.floor(n));
  }

  /** Returns false and changes nothing if the player cannot afford it. */
  spendCoins(n: number): boolean {
    if (n < 0 || this.muenzen < n) return false;
    this.muenzen -= n;
    return true;
  }

  addTickets(n: number): void {
    this.tickets = Math.max(0, this.tickets + Math.floor(n));
  }

  /**
   * Tickets buy futures (economy.md): the Preistheke and the Saalchef door. They are
   * anonymous, so unlike coins the House can neither tax nor confiscate them — which is
   * exactly why ticket-heavy builds are the answer to high VERDACHT.
   */
  spendTickets(n: number): boolean {
    if (n < 0 || this.tickets < n) return false;
    this.tickets -= n;
    return true;
  }

  spendGlueck(n = 1): boolean {
    if (this.glueck < n) return false;
    this.glueck -= n;
    return true;
  }

  /** Called on every dud/loss. Emits when a full point of GLÜCK comes back. */
  registerLoss(emit: (e: SimEvent) => void): void {
    if (this.glueck >= GLUECK_MAX) return;
    this.pity++;
    if (this.pity >= this.pityPerGlueck) {
      this.pity -= this.pityPerGlueck;
      this.glueck = Math.min(GLUECK_MAX, this.glueck + 1);
      emit({ t: 'glueckRegen', amount: 1 });
    }
  }

  addVerdacht(delta: number, reason: string, emit: (e: SimEvent) => void): void {
    const before = this.verdacht;
    this.verdacht = Math.max(0, Math.min(VERDACHT_MAX, this.verdacht + delta));
    if (this.verdacht !== before) emit({ t: 'verdacht', delta: this.verdacht - before, reason });
  }

  /** 90+: coins are refused; the engine sputters but the economy keeps moving. */
  get hausverbot(): boolean {
    return this.verdacht >= VERDACHT_THRESHOLDS.hausverbot;
  }

  /**
   * Die Abrechnung. Partial payment is taken in kind — losing is a slope, not a cliff.
   * `seizable` is the ordered list of things Der Croupier may take (tokens, then
   * enchantments, then a cabinet); the caller supplies value estimates.
   */
  settle(
    due: number,
    seizable: { id: string; worth: number }[],
    emit: (e: SimEvent) => void,
  ): { paid: number; confiscated: string[] } {
    const paid = Math.min(this.muenzen, due);
    this.muenzen -= paid;
    let shortfall = due - paid;
    const confiscated: string[] = [];
    for (const item of seizable) {
      if (shortfall <= 0) break;
      confiscated.push(item.id);
      shortfall -= item.worth;
    }
    emit({ t: 'abrechnung', due, paid, confiscated });
    // paying up quietly cools the room a little
    if (shortfall <= 0) this.addVerdacht(-5, 'Abrechnung beglichen', emit);
    return { paid, confiscated };
  }

  pityDebt(): number {
    return this.pity;
  }
}
